import React, { FC, useCallback, useEffect, useRef } from 'react';
import { OverlayWrapper } from 'components/overlay-wrapper';
import { Button } from 'ds/components/button';
import { useLocale } from 'locale';
import styled from '@emotion/styled';
import { Text } from 'ds/components/typography';
import CloseIcon from 'assets/svg/close.svg';
import { CompletionType, DealType, IAutocompleteEntry, MarketplaceType } from 'utils/entities';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { dealTypeSelector } from 'store/state/selectors/search';
import { navigateTo } from 'store/state/router/actions';
import { NavigateToFn, Route, State as RouteState } from 'config/routes';
import { DEAL_TYPE_TO_APP_MODE, marketplaceSelector, routeSelector } from 'store/state/selectors/router';
import { MultiLocationSearch } from 'components/navigation/subheader/MultiLocationSearch';
import { ISaveSearch } from 'store/sagas/apiService/types';
import { searchRouteByMarketplace } from 'utils/marketplaceRoutes';
import config from 'config';
import { searchDocEntriesSelector, searchHistorySelector } from 'store/state/domainData/selectors';
import { useSendEvent } from 'analytics/useSendEvent';
import { buildSearchPayload } from 'components/search-widget/helpers';

const FOOTER_HEIGHT = 72;
const HEADER_HEIGHT = 116;

const Wrapper = styled.div`
  padding: ${HEADER_HEIGHT}px 0 ${FOOTER_HEIGHT}px;
  position: relative;
  z-index: 2;
  ${({ theme }) => theme.media.showAt({ to: 2 }) `
    z-index: 20;
    &:before {
      content: '';
      position: fixed;
      background: ${theme.colors.neutrals.white};
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: -1;
    }
  `}
`;

const MobileOverlayContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.neutrals.white};
  min-height: calc(100vh - ${HEADER_HEIGHT + FOOTER_HEIGHT}px);
  & > section {
    padding-right: ${({ theme }) => theme.spacing(2)};
    padding-left: ${({ theme }) => theme.spacing(2)};
  }
`;

const ControlsFooter = styled.div`
  ${({ theme }) => `
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 ${theme.spacing(2)};
    height: ${FOOTER_HEIGHT}px;
    background: ${theme.colors.neutrals.white};
    z-index: 3;
    border-top: 1px solid ${theme.colors.neutrals.grey8};
  `}
`;

const FixedWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${HEADER_HEIGHT / 2}px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  background: ${({ theme }) => theme.colors.neutrals.white};
  position: relative;
  span {
    position: absolute;
    top: 50%;
    height: 40px;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -20px;
    left: 16px;
  }
`;

const Subheader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  background: ${({ theme }) => theme.colors.neutrals.white};
`;

const DealTypeTab = styled(Text)<{ isActive?: boolean }>`
  font-weight: 700;
  width: 50%;
  border-bottom: 2px solid ${({ isActive, theme }) => (isActive ? theme.colors.neutrals.grey1 : 'transparent')};
  margin-bottom: -1px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${HEADER_HEIGHT / 2}px;
`;

const LocationSearchWrapper = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding: 0 ${({ theme }) => theme.spacing(3)};
`;

interface CombinedFiltersSearchMobileWrapperProps {
  onClose: () => void;
  onCommit: () => void;
  onReset: () => void;
  dealType: DealType;
  navigate: NavigateToFn;
  route: RouteState;
  searchHistory: ISaveSearch[];
  marketplace: MarketplaceType;
  selectedLocations: IAutocompleteEntry[];
}

const dealTypeOptions = [ DealType.Buy, DealType.Rent ];

const BaseCombinedFiltersSearchMobileWrapper: FC<CombinedFiltersSearchMobileWrapperProps> = props => {
  const { children, onClose, onReset, onCommit, dealType, navigate, route, marketplace, searchHistory, selectedLocations } = props;
  const { t } = useLocale();
  const sendEvent = useSendEvent();

  const initialParamsFallback = useRef(null);
  const isDirectLocationMode = route.params.mobileFiltersPopupActive === 'location';

  useEffect(() => {
    if (initialParamsFallback.current === null) {
      initialParamsFallback.current = { ...route.params };
    }

    return () => {
      initialParamsFallback.current = null;
    };
  }, []);

  const handleDealTypeChange = useCallback(() => {
    const nextDealType = dealType === DealType.Buy ? DealType.Rent : DealType.Buy;

    navigate(route.name, {
      ...route.params,
      dealType: nextDealType,
      filters: {
        ...route.params.filters,
        dealType: nextDealType,
        ppmRange: undefined,
        priceRange: undefined,
      },
    }, { replace: true });
  }, [ navigate, route, dealType ]);

  const handleCancel = useCallback(() => {
    if (initialParamsFallback.current) {
      navigate(route.name, { ...initialParamsFallback.current, mobileFiltersPopupActive: undefined }, { replace: true });
    }

    onClose();
  }, [ navigate, route, onClose ]);

  const handleSearch = useCallback((value: string[], projectType?: CompletionType) => {
    if (projectType) {
      const params = { id: value[0] };
      navigate(projectType === CompletionType.CommercialProject ? Route.ProjectPageCommercial :  Route.ProjectPage, params);
    }
    else {
      navigate(searchRouteByMarketplace(marketplace), {
        ...route.params,
        ...{ term: value && value.length ? value : [ config.cityTerm ] },
        page: undefined,
        bbox: undefined,
        tracking_search_source: 'new_search',
        mobileFiltersPopupActive: 'filters',
      }, { replace: true });
    }
  }, [ route, navigate, marketplace ]);

  const handleApplyFilters = useCallback(() => {
    onCommit();
    onClose();

    sendEvent('search_submit', 'search', {
      event: {
        source: 'search_results',
      },
      search: buildSearchPayload({
        selectedItems: selectedLocations,
        searchMode: DEAL_TYPE_TO_APP_MODE[route.params.dealType],
      }),
    });
  }, [ route, sendEvent, selectedLocations ]);

  const handleResetFilters = useCallback(() => {
    onReset(); // solely for the event

    navigate(searchRouteByMarketplace(marketplace), {
      ...route.params,
      term: [ config.cityTerm ],
      filters: { dealType: route.params.dealType },
      page: undefined,
      bbox: undefined,
      tracking_search_source: 'new_search',
      mobileFiltersPopupActive: undefined,
    }, { replace: true });

    onClose();
  }, [ route, navigate, marketplace, onClose, onReset ]);

  return (
    <OverlayWrapper data-auto="mobile-filter-popup">
      <Wrapper>
        <FixedWrapper>
          <Header>
            <Text weight="bold">{t('combinedFilters.header')}</Text>
            <span onClick={handleCancel}>
              <CloseIcon width={16} height={16} />
            </span>
          </Header>
          <Subheader>
            {dealTypeOptions.map(type => (
              <DealTypeTab key={type} isActive={dealType === type} onClick={dealType === type ? undefined : handleDealTypeChange}>
                {t('html.dealTypeFragment', { dealType: type })}
              </DealTypeTab>
            ))}
          </Subheader>
        </FixedWrapper>
        <MobileOverlayContainer>
          <LocationSearchWrapper>
            <Text weight="bold">{t('combinedFilters.locationSearchLabel')}</Text>
            <MultiLocationSearch
              onSearch={handleSearch}
              marketplace={marketplace}
              searchHistory={searchHistory}
              insideFiltersSearchMode
              onCancel={isDirectLocationMode ? handleCancel : undefined}
            />
          </LocationSearchWrapper>
          {children}
          <ControlsFooter>
            <Button data-auto="filterClearButton" size="extraLargeIncreasedPadding" mode="ghost-black-text" onClick={handleResetFilters}>
              {t('combinedFilters.clearAll')}
            </Button>
            <Button data-auto="filterSubmitButton" size="extraLargeIncreasedPadding" onClick={handleApplyFilters}>
              {t('combinedFilters.apply')}
            </Button>
          </ControlsFooter>
        </MobileOverlayContainer>
      </Wrapper>
    </OverlayWrapper>
  );
};

const mapStateToProps = (state: State) => ({
  dealType: dealTypeSelector(state),
  route: routeSelector(state),
  marketplace: marketplaceSelector(state),
  searchHistory: searchHistorySelector(state),
  selectedLocations: searchDocEntriesSelector(state),
});

const mapDispatchToProps = {
  navigate: navigateTo,
};

export const CombinedFiltersSearchMobileWrapper = connect(mapStateToProps, mapDispatchToProps)(BaseCombinedFiltersSearchMobileWrapper);
